/**
 * @module app.core
 */

import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { CustomFieldService, ICustomFieldSearchFilters } from 'app/core/services/custom-field-service';
import { Toaster } from 'app/shared/lib/toaster';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { localSort } from 'app/shared/lib/sorting';
import { validate } from 'app/shared/elements/ess-rule';
import { IPopup } from 'app/shared/elements/popup-base';
import { deepClone, isNullOrUndefined } from 'app/shared/lib/utilities';
import { MenuRefreshEvent } from 'app/shared/lib/events';
import { EventAggregator } from 'aurelia-event-aggregator';
import * as Core from 'app/core/models/essette-api';

interface CustomFieldWithDefinitionLabel extends Core.CustomField {
    definitionLabel?: string | null;
    hiddenSortLabel?: string | null;
}

@autoinject()
export class UserDefinedFieldsGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    protected moduleCode?: string;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    protected targetCode?: string;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    protected title?: string;

    protected editedCustomFieldDefinition: Core.CustomFieldDefinition = {};
    protected customFields: Core.CustomField[] = [];
    protected searchFilters: ICustomFieldSearchFilters = { moduleCode: '' };

    private localSorting = new Sorting();

    protected validate = validate;
    protected isValid = false;

    ////****************** Element References ***************************************////
    private $modal!: IPopup;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CustomFieldService,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.searchFilters.moduleCode = this.moduleCode;
        this.searchFilters.targetCode = this.targetCode;
    }

    protected async attached(): Promise<void> {
        await this.reload();
    }

    ////****************** Events ***************************************************////
    protected async handleFormSuccess(event: any): Promise<void> {
        this.$modal.close();
        if(this.editedCustomFieldDefinition.property?.type === 'lookup') this.refreshMainMenu();
        await this.reload();
        // refresh main menu only if edited UDF is a lookup
        if(this.editedCustomFieldDefinition.property?.type === 'lookup') this.refreshMainMenu();
    }

    /** Handles the grid's sort event */
    protected async applyLocalSort(event: SortChangeEvent, customFields: Core.CustomField[]): Promise<void> {
        const { property, direction } = event.detail;
        const { localSorting } = this;
        if(direction === null) {
            localSorting.remove(property);
        } else {
            localSorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        localSort(customFields, localSorting);
        if(this.localSorting.entries.length == 0) {
            this.reload();
            //Call stop Propagation to prevent parent table from reload and losing sort
            event.stopPropagation();
        };
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadCustomFieldDefinitions();
    }

    protected saveCustomField(customField: Core.CustomField = {}): void {
        this.editedCustomFieldDefinition = customField.definition ?
            deepClone(customField.definition) :
            {
                field: { id: customField.id },
                property: { type: customField.type?.code }
            };
        this.$modal.open();
    }

    protected refreshMainMenu(): void {
        this.events.publish(new MenuRefreshEvent);
        this.accountContext.clear();
    }

    // Set Custom Field Definition Labels and Locally Sort
    private setCustomFieldDefinitionLabels(customFields: CustomFieldWithDefinitionLabel[]): void {
        customFields.forEach(customField => {
            if(!customField.type?.description || isNullOrUndefined(customField.index)) return;
            customField.definitionLabel = `${customField.type.description} ${customField.index}`;
            // hidden sort label adds a leading 0 to single-digit indices for accurate sorting
            const sortIndex = customField.index.toString().padStart(2, '0');
            customField.hiddenSortLabel = `${customField.type.description} ${sortIndex}`;
        });
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadCustomFieldDefinitions(): Promise<void> {

        const defaultSort = new Sorting();
        defaultSort.entries = [{
            reference: 'hiddenSortLabel',
            direction: SortDirection.Ascending
        }];

        const options: StandardLoadOptions = {
            all: true,
            includeInactive: true
        };

        const includes = ['definition', 'type', 'target'];

        try {
            this.state.beginLoad();
            const response = await this.service.loadCustomFields(this.searchFilters, options, includes);
            this.setCustomFieldDefinitionLabels(<CustomFieldWithDefinitionLabel[]>response.results);
            this.customFields = response.results;
            localSort(this.customFields, defaultSort);
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateCustomFieldDefinition(customFieldDefinition: Core.CustomFieldDefinition): Promise<void> {
        if(!customFieldDefinition.id) return;

        // Show confirmation dialog only if deactivating
        if(customFieldDefinition.isActive) {
            const result = await confirmation.showDeactivate('core:customField.$long', 1);
            if(result.dialogResponse !== confirmation.options.OK) return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateCustomFieldDefinition(customFieldDefinition.id, !customFieldDefinition.isActive);
            isSuccessful = response.isSuccessful;
            if(customFieldDefinition.property?.type === 'lookup') this.refreshMainMenu();
            await this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('core:customField.$long', !customFieldDefinition.isActive, isSuccessful);
            this.state.endSave();
        }
    }
}
